import { useMemo, useState } from 'react';
import { LANGUAGE_AUTONYMS } from '../../../i18n';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { Button, ButtonGroup, ErrorSummary, Radios, RequiredFieldsNote } from '../../../components/idsk';
import { FormRenderer, errorSummaryItems, hasRequiredFields, type Lang } from '../../../form-engine/react/FormRenderer';
import type { EvaluationContext, EvaluationResult, FormDefinition, FormValues } from '../../../form-engine';
import { todayIso } from '../../../lib/format';

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Sample context for previews: today, a timeframe in two weeks and two sample team members. */
export function previewContext(t: (k: string, o?: Record<string, unknown>) => string): EvaluationContext {
  const today = todayIso();
  return {
    today,
    timeframe: { from: addDays(today, 14), to: addDays(today, 18) },
    teamMembers: [
      { id: 'preview-1', name: t('builder.preview.member', { n: 1 }) },
      { id: 'preview-2', name: t('builder.preview.member', { n: 2 }) },
    ],
  };
}

/**
 * "Náhľad": renders the form exactly as users will see it, in Slovak or English, with working rules.
 * The preview has its own i18n instance so the form chrome follows the preview language.
 */
export function FormPreview({ formType, definition, context }: { formType: string; definition: FormDefinition; context?: EvaluationContext }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState<Lang>(i18n.resolvedLanguage === 'en' ? 'en' : 'sk');
  const [values, setValues] = useState<FormValues>({});
  const [resetKey, setResetKey] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [result, setResult] = useState<EvaluationResult | null>(null);
  const previewI18n = useMemo(() => {
    const instance = i18n.cloneInstance({ lng: lang });
    void instance.changeLanguage(lang);
    return instance;
  }, [lang]);
  const ctx = useMemo(() => context ?? previewContext(t), [context, t]);
  const items = showErrors && result ? errorSummaryItems(formType, definition, result.errors, previewI18n.t.bind(previewI18n), lang) : [];
  return (
    <>
      <Radios
        name="preview-lang"
        idPrefix="preview-lang"
        legend={t('builder.preview.language')}
        legendClassName="govuk-fieldset__legend--s"
        inline
        items={[
          { value: 'sk', label: LANGUAGE_AUTONYMS.sk, lang: 'sk' },
          { value: 'en', label: LANGUAGE_AUTONYMS.en, lang: 'en' },
        ]}
        value={lang}
        onChange={(v) => setLang(v as Lang)}
      />
      <div className="app-preview-frame" lang={lang} data-testid="form-preview">
        <I18nextProvider i18n={previewI18n}>
          {items.length > 0 && <ErrorSummary errors={items} />}
          {hasRequiredFields(definition) && <RequiredFieldsNote />}
          <FormRenderer
            formType={`preview-${formType}`}
            definition={definition}
            values={values}
            onChange={setValues}
            context={ctx}
            lang={lang}
            showErrors={showErrors}
            resetKey={resetKey}
            onEvaluate={setResult}
          />
        </I18nextProvider>
      </div>
      <ButtonGroup>
        <Button variant="secondary" onClick={() => setShowErrors(true)}>
          {t('builder.preview.check')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setValues({});
            setShowErrors(false);
            setResetKey((k) => k + 1);
          }}
        >
          {t('builder.preview.reset')}
        </Button>
      </ButtonGroup>
      {result && (
        <p className="govuk-body" role="status">
          {result.complete ? t('forms.complete') : t('forms.incompleteCount', { count: result.errors.length })}
        </p>
      )}
    </>
  );
}
